import type { AiTokenUsageTimeSeriesPoint } from '@repo/api-codegen';

type UpdatedAiUsageTimeSeriesPoint = Omit<
	AiTokenUsageTimeSeriesPoint,
	'timestamp'
> & {
	timestamp: Date;
};

export function processTimeseriesData<T extends { timestamp: string }>(
	points: T[] | undefined
): Array<T & { timestamp: Date }> {
	if (!points) return [];

	return points
		.map(
			(point) =>
				({
					...point,
					timestamp: new Date(point.timestamp),
				}) as T & { timestamp: Date }
		)
		.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

export function getGroupKeys<
	T extends { group_data?: Record<string, unknown> | null },
>(chartData: T[]): string[] {
	if (chartData.length === 0) return [];

	const keys = new Set<string>();
	chartData.forEach((point) => {
		if (point.group_data) {
			Object.keys(point.group_data).forEach((key) => keys.add(key));
		}
	});
	return Array.from(keys).sort();
}

export function getTopGroupKeys(
	chartData: UpdatedAiUsageTimeSeriesPoint[],
	getValue: (
		groupData: NonNullable<AiTokenUsageTimeSeriesPoint['group_data']>[string]
	) => number,
	maxUsers: number
): string[] {
	if (chartData.length === 0) return [];

	const groupAggregates = new Map<string, number>();

	chartData.forEach((point) => {
		if (point.group_data) {
			Object.entries(point.group_data).forEach(([groupKey, groupData]) => {
				const currentTotal = groupAggregates.get(groupKey) ?? 0;
				groupAggregates.set(groupKey, currentTotal + getValue(groupData));
			});
		}
	});

	// Sort by aggregate value (descending) and take top groups
	return Array.from(groupAggregates.entries())
		.sort((a, b) => b[1] - a[1])
		.slice(0, maxUsers)
		.map(([key]) => key)
		.sort(); // Sort alphabetically so same users get same colors across charts
}
